// API utilities

import { supabase } from '../lib/supabase';
import { ERROR_MESSAGES } from './constants';
import type { Player, Team, Practice, Drill, DepthChartEntry } from '../types';

// Generic API response type
export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  success: boolean;
}

// Generic error handler
export const handleApiError = (error: unknown): string => {
  console.error('API Error:', error);
  
  if (error?.message) {
    return error.message;
  }
  
  if (error?.code) {
    switch (error.code) {
      case 'PGRST116':
        return ERROR_MESSAGES.NOT_FOUND;
      case '23505':
        return 'This record already exists.';
      case '23503':
        return 'Cannot delete this record because it is referenced by other data.';
      default:
        return ERROR_MESSAGES.GENERIC_ERROR;
    }
  }
  
  return ERROR_MESSAGES.GENERIC_ERROR;
};

// Generic API wrapper
export const apiWrapper = async <T>(
  operation: () => Promise<unknown>
): Promise<ApiResponse<T>> => {
  try {
    const result = await operation();
    
    if (result.error) {
      return {
        data: null,
        error: handleApiError(result.error),
        success: false
      };
    }
    
    return {
      data: result.data,
      error: null,
      success: true
    };
  } catch (error) {
    return {
      data: null,
      error: handleApiError(error),
      success: false
    };
  }
};

// Team API functions
export const teamApi = {
  getAll: () => apiWrapper<Team[]>(() => 
    supabase.from('teams').select('*')
  ),
  
  getById: (id: string) => apiWrapper<Team>(() => 
    supabase.from('teams').select('*').eq('id', id).single()
  ),
  
  create: (team: Omit<Team, 'id'>) => apiWrapper<Team>(() => 
    supabase.from('teams').insert(team).select().single()
  ),
  
  update: (id: string, updates: Partial<Team>) => apiWrapper<Team>(() => 
    supabase.from('teams').update(updates).eq('id', id).select().single()
  ),
  
  delete: (id: string) => apiWrapper<void>(() => 
    supabase.from('teams').delete().eq('id', id)
  )
};

// Player API functions
export const playerApi = {
  getByTeam: (teamId: string) => apiWrapper<Player[]>(() => 
    supabase.from('players').select('*').eq('team_id', teamId)
  ),
  
  getById: (id: string) => apiWrapper<Player>(() => 
    supabase.from('players').select('*').eq('id', id).single()
  ),
  
  create: (player: Omit<Player, 'id'>) => apiWrapper<Player>(() => 
    supabase.from('players').insert(player).select().single()
  ),
  
  update: (id: string, updates: Partial<Player>) => apiWrapper<Player>(() => 
    supabase.from('players').update(updates).eq('id', id).select().single()
  ),
  
  delete: (id: string) => apiWrapper<void>(() => 
    supabase.from('players').delete().eq('id', id)
  ),
  
  bulkCreate: (players: Omit<Player, 'id'>[]) => apiWrapper<Player[]>(() => 
    supabase.from('players').insert(players).select()
  )
};

// Practice API functions
export const practiceApi = {
  getByTeam: (teamId: string) => apiWrapper<Practice[]>(() => 
    supabase.from('practices').select('*').eq('team_id', teamId).order('date', { ascending: false })
  ),
  
  getById: (id: string) => apiWrapper<Practice>(() => 
    supabase.from('practices').select('*').eq('id', id).single()
  ),
  
  create: (practice: Omit<Practice, 'id'>) => apiWrapper<Practice>(() => 
    supabase.from('practices').insert(practice).select().single()
  ),
  
  update: (id: string, updates: Partial<Practice>) => apiWrapper<Practice>(() => 
    supabase.from('practices').update(updates).eq('id', id).select().single()
  ),
  
  delete: (id: string) => apiWrapper<void>(() => 
    supabase.from('practices').delete().eq('id', id)
  )
};

// Drill API functions
export const drillApi = {
  getByPractice: (practiceId: string) => apiWrapper<Drill[]>(() => 
    supabase.from('drills').select('*').eq('practice_id', practiceId).order('created_at')
  ),
  
  getById: (id: string) => apiWrapper<Drill>(() => 
    supabase.from('drills').select('*').eq('id', id).single()
  ),
  
  create: (drill: Omit<Drill, 'id'>) => apiWrapper<Drill>(() => 
    supabase.from('drills').insert(drill).select().single()
  ),
  
  update: (id: string, updates: Partial<Drill>) => apiWrapper<Drill>(() => 
    supabase.from('drills').update(updates).eq('id', id).select().single()
  ),
  
  delete: (id: string) => apiWrapper<void>(() => 
    supabase.from('drills').delete().eq('id', id)
  ),
  
  bulkCreate: (drills: Omit<Drill, 'id'>[]) => apiWrapper<Drill[]>(() => 
    supabase.from('drills').insert(drills).select()
  )
};

// Depth Chart API functions
export const depthChartApi = {
  getByTeam: (teamId: string) => apiWrapper<DepthChartEntry[]>(() => 
    supabase.from('depth_chart_entries').select('*').eq('team_id', teamId)
  ),
  
  upsert: (entries: DepthChartEntry[]) => apiWrapper<DepthChartEntry[]>(() => 
    supabase.from('depth_chart_entries').upsert(entries).select()
  ),
  
  deleteByTeam: (teamId: string) => apiWrapper<void>(() => 
    supabase.from('depth_chart_entries').delete().eq('team_id', teamId)
  ),
  
  deleteByPosition: (teamId: string, position: string, unit: string) => apiWrapper<void>(() => 
    supabase.from('depth_chart_entries')
      .delete()
      .eq('team_id', teamId)
      .eq('position', position)
      .eq('unit', unit)
  )
};

// Utility function for retrying failed requests
export const retryRequest = async <T>(
  operation: () => Promise<ApiResponse<T>>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<ApiResponse<T>> => {
  let lastError: string = ERROR_MESSAGES.GENERIC_ERROR;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      
      if (result.success) {
        return result;
      }
      
      lastError = result.error || ERROR_MESSAGES.GENERIC_ERROR;
      
      // Don't retry on certain errors
      if (lastError.includes('not found') || lastError.includes('unauthorized')) {
        break;
      }
      
      // Wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    } catch (error) {
      lastError = handleApiError(error);
    }
  }
  
  return {
    data: null,
    error: lastError,
    success: false
  };
}; 